/**
 * MyGurukulPage — Iter 60 (Jul 2026)
 *
 * Consolidated hub for the five daily study destinations that used to
 * sit on the top nav (Dashboard / Graphics / Flashcards / Quiz / Gamify).
 * Rendered as a grid of colourful gradient cards — visual language
 * matches the Gamify subject cards so students recognise the "pick your
 * path" energy.
 *
 * All routing decisions stay ONE click deep — the card is the destination.
 * Future destinations (e.g. Voice Tutor, Study Circle) just append here.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useActiveScope from '@/lib/useActiveScope';
import { fetchMockConfig } from '@/lib/mockExams';
import { BarChart3, Sparkles, BookOpen, Brain, Gamepad2, ArrowRight, ScrollText, ClipboardCheck, FileText, Presentation, Timer, NotebookPen } from 'lucide-react';

const CARDS = [
  {
    id: 'summary',
    label: 'Summary',
    tagline: 'The whole chapter in points you can actually remember.',
    icon: FileText,
    path: '/summary',
    gradient: 'from-indigo-500 via-blue-600 to-sky-600',
  },
  {
    id: 'notes',
    label: 'Notes Taken',
    tagline: 'Every point Study Buddy noted for you, chapter by chapter.',
    icon: NotebookPen,
    path: '/notes',
    gradient: 'from-amber-400 via-orange-500 to-rose-500',
  },
  {
    id: 'slides',
    label: 'Slides',
    tagline: 'Flip through the chapter — one idea per slide.',
    icon: Presentation,
    path: '/slides',
    gradient: 'from-violet-500 via-purple-600 to-fuchsia-600',
  },
  {
    id: 'mock-exam',
    label: 'Mock Exam',
    tagline: 'A timed rehearsal, marked like the real paper.',
    icon: Timer,
    path: '/mock-exam',
    gradient: 'from-rose-500 via-red-500 to-orange-500',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    tagline: 'Your streaks, points & progress at a glance.',
    icon: BarChart3,
    path: '/dashboard',
    gradient: 'from-sky-500 via-indigo-500 to-violet-600',
  },
  {
    id: 'gallery',
    label: 'Graphics',
    tagline: 'Infographics + cinegraphics for every chapter.',
    icon: Sparkles,
    path: '/gallery',
    gradient: 'from-fuchsia-500 via-pink-500 to-rose-500',
  },
  {
    id: 'flashcards',
    label: 'Flashcards',
    tagline: 'Bite-size cards to lock the concepts in.',
    icon: BookOpen,
    path: '/flashcards',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
  },
  {
    id: 'quiz',
    label: 'Quiz',
    tagline: 'Test yourself, chapter by chapter.',
    icon: Brain,
    path: '/quiz',
    gradient: 'from-amber-500 via-orange-500 to-red-500',
  },
  {
    id: 'growth',
    label: 'My Growth',
    tagline: 'Badges, streaks and how far you\'ve come — yours alone.',
    icon: Sparkles,
    path: '/growth',
    gradient: 'from-amber-400 via-orange-500 to-rose-500',
  },
  {
    id: 'worksheets',
    label: 'Worksheets',
    tagline: 'Printable practice for every chapter you\'re studying.',
    icon: ClipboardCheck,
    path: '/worksheets',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
  },
  {
    id: 'question-bank',
    label: 'Question Bank',
    tagline: 'Real papers from real exams — add yours, use everyone else\'s.',
    icon: ScrollText,
    path: '/question-bank',
    gradient: 'from-slate-700 via-slate-800 to-slate-900',
  },
  {
    id: 'gamify',
    label: 'Gamify',
    tagline: 'Puzzles + mazes powered by your syllabus.',
    icon: Gamepad2,
    path: '/gamify',
    gradient: 'from-purple-500 via-violet-500 to-indigo-600',
  },
];

// Eleven cards in one wall had stopped meaning anything. Five named groups, in
// the order a student actually moves through a chapter: see where you stand,
// learn it, test it, enjoy it, then help us make it better.
const SECTIONS = [
  { id: 'dashboard', title: 'Dashboard', blurb: 'Where you stand right now.',
    cards: ['dashboard', 'growth'] },
  { id: 'explore', title: 'Explore', blurb: 'Learn the chapter your way.',
    cards: ['summary', 'notes', 'slides', 'gallery'] },
  { id: 'test-yourself', title: 'Test Yourself', blurb: 'Find out what has actually stuck.',
    cards: ['quiz', 'flashcards', 'worksheets', 'mock-exam'] },
  { id: 'learning-with-fun', title: 'Learning With Fun', blurb: 'Same syllabus, played instead of read.',
    cards: ['gamify'] },
  { id: 'help-us', title: 'Help Us Improve GurukulVeda', blurb: 'Share a paper and every student gains.',
    cards: ['question-bank'] },
];

const BY_ID = Object.fromEntries(CARDS.map(c => [c.id, c]));

export default function MyGurukulPage() {
  const navigate = useNavigate();
  const { subject } = useActiveScope();
  const [mockReady, setMockReady] = useState(false);

  // The Mock Exam card becomes the headline the moment every chapter of the
  // student's current subject is done — that is when a rehearsal is worth it.
  useEffect(() => {
    if (!subject) return;
    fetchMockConfig(subject)
      .then(c => setMockReady(!!c.subject_mock_unlocked && c.mocks_remaining > 0))
      .catch(() => { /* not entitled or module off — stay quiet */ });
  }, [subject]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="my-gurukul-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
          My Gurukul
        </h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          Everything you need to master this chapter — pick where you want to go next.
        </p>
      </div>

      <div className="space-y-8 sm:space-y-10">
        {SECTIONS.map(section => {
          const cards = section.cards.map(id => BY_ID[id]).filter(Boolean);
          if (!cards.length) return null;
          return (
            <section key={section.id} data-testid={`my-gurukul-section-${section.id}`}>
              <div className="flex items-baseline gap-3 mb-3 sm:mb-4">
                <h2 className="font-heading text-base md:text-lg font-bold text-slate-900">{section.title}</h2>
                <span className="h-px flex-1 bg-slate-200" />
                <p className="font-body text-[11px] sm:text-xs text-slate-400 hidden sm:block">{section.blurb}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {cards.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(c.path)}
            data-testid={`my-gurukul-card-${c.id}`}
            className={`relative overflow-hidden rounded-3xl p-5 sm:p-6 text-left shadow-md hover:shadow-xl transition-all hover:-translate-y-1 active:scale-[0.98] bg-gradient-to-br ${c.gradient} text-white group ${
              c.id === 'mock-exam' && mockReady ? 'ring-4 ring-amber-300 sm:col-span-2 lg:col-span-1' : ''}`}
          >
            {c.id === 'mock-exam' && mockReady && (
              <span className="absolute top-3 right-3 z-10 text-[10px] font-heading uppercase tracking-widest bg-amber-300 text-amber-950 px-2 py-1 rounded-full"
                data-testid="mock-exam-unlocked-badge">
                Ready for you
              </span>
            )}
            {/* Soft geometric glow */}
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-10 -left-6 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative flex items-start justify-between">
              <c.icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/90" strokeWidth={1.75} />
              <span className="inline-flex items-center gap-1 text-[11px] font-heading uppercase tracking-widest text-white/85 bg-white/15 px-2.5 py-1 rounded-full backdrop-blur-sm group-hover:bg-white/25 transition-colors">
                Open <ArrowRight className="w-3 h-3" />
              </span>
            </div>

            <p className="relative mt-6 sm:mt-8 text-2xl sm:text-3xl font-display font-bold tracking-tight">
              {c.label}
            </p>
            <p className="relative mt-1 text-[12px] sm:text-sm font-body text-white/85 max-w-[240px]">
              {c.tagline}
            </p>
          </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
